import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/hooks/useAuth";
import { ImpersonationProvider } from "@/hooks/useImpersonation";
import { PartnerBrandingProvider } from "@/contexts/PartnerBrandingContext";
import { GoogleAnalyticsPageTracker } from "@/components/GoogleAnalytics";
import { PostHogProvider } from "@/components/PostHogProvider";
import { PostHogPageTracker } from "@/components/PostHogPageTracker";
import { GlobalImpersonationBanner } from "@/components/app/GlobalImpersonationBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import LoadingFallback from "@/components/LoadingFallback";

// Critical pages - loaded immediately
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages - Auth
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));

// Lazy-loaded pages - Dashboard & Workspace
const Onboarding = lazy(() => import("./pages/Onboarding"));
const TributeDashboard = lazy(() => import("./pages/TributeDashboard"));
const TributeWorkspace = lazy(() => import("./pages/TributeWorkspace"));
const Account = lazy(() => import("./pages/Account"));

// Lazy-loaded pages - Public Tributes
const PublicTribute = lazy(() => import("./pages/PublicTribute"));
const FeaturedTributesGallery = lazy(() => import("./pages/FeaturedTributesGallery"));

// Lazy-loaded pages - Marketing
const WhyLivingTributePage = lazy(() => import("./pages/WhyLivingTributePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const SupportCenter = lazy(() => import("./pages/SupportCenter"));
const PlanAheadPage = lazy(() => import("./pages/PlanAheadPage"));
const PetsPage = lazy(() => import("./pages/PetsPage"));
const GiftTribute = lazy(() => import("./pages/GiftTribute"));
const TributeVideoPage = lazy(() => import("./pages/TributeVideoPage"));
const DigitalMemorialPage = lazy(() => import("./pages/DigitalMemorialPage"));
const CelebrationOfLifeVideoPage = lazy(() => import("./pages/CelebrationOfLifeVideoPage"));
const OnlineMemorialPagePage = lazy(() => import("./pages/OnlineMemorialPagePage"));
const HowToMakeTributeVideoPage = lazy(() => import("./pages/HowToMakeTributeVideoPage"));
const PhotosToLifePage = lazy(() => import("./pages/PhotosToLifePage"));
const CollaboratePage = lazy(() => import("./pages/CollaboratePage"));
const CraftObituaryPage = lazy(() => import("./pages/CraftObituaryPage"));

// Lazy-loaded pages - Invites & Quick Drop
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const QuickDropPage = lazy(() => import("./pages/QuickDropPage"));

// Lazy-loaded pages - Partners
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PartnerApply = lazy(() => import("./pages/PartnerApply"));
const PartnerPending = lazy(() => import("./pages/PartnerPending"));
const PartnerTermsPage = lazy(() => import("./pages/PartnerTermsPage"));
const PartnerLanding = lazy(() => import("./pages/PartnerLanding"));
const PartnerDashboard = lazy(() => import("./pages/partner/PartnerDashboard"));
const PartnerUsers = lazy(() => import("./pages/partner/PartnerUsers"));
const PartnerTributes = lazy(() => import("./pages/partner/PartnerTributes"));
const PartnerRevenue = lazy(() => import("./pages/partner/PartnerRevenue"));
const PartnerSettings = lazy(() => import("./pages/partner/PartnerSettings"));
const PartnerSupport = lazy(() => import("./pages/partner/PartnerSupport"));

// Lazy-loaded pages - Admin
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminTributes = lazy(() => import("./pages/admin/AdminTributes"));
const AdminTributeDetail = lazy(() => import("./pages/admin/AdminTributeDetail"));
const AdminGifts = lazy(() => import("./pages/admin/AdminGifts"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminPartnerDetail = lazy(() => import("./pages/admin/AdminPartnerDetail"));
const AdminSubdomainDetail = lazy(() => import("./pages/admin/AdminSubdomainDetail"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminPayouts = lazy(() => import("./pages/admin/AdminPayouts"));
const AdminPayoutDetail = lazy(() => import("./pages/admin/AdminPayoutDetail"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PostHogProvider>
          <AuthProvider>
            <ImpersonationProvider>
              <PartnerBrandingProvider>
                <TooltipProvider>
                  <ErrorBoundary>
                    <GlobalImpersonationBanner />
                    <GoogleAnalyticsPageTracker />
                    <PostHogPageTracker />
                    <ScrollToTop />
                    <Toaster />
                    <Sonner />
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        {/* Critical routes - no lazy loading */}
                        <Route path="/" element={<Index />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        
                        {/* Auth routes */}
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/oauth/callback" element={<OAuthCallback />} />
                        
                        {/* Dashboard & Workspace */}
                        <Route path="/onboarding" element={<Onboarding />} />
                        <Route path="/dashboard" element={<TributeDashboard />} />
                        <Route path="/tribute/:id" element={<TributeWorkspace />} />
                        <Route path="/account" element={<Account />} />
                        
                        {/* Public Tributes */}
                        <Route path="/see/example" element={<FeaturedTributesGallery />} />
                        <Route path="/see/:slug/*" element={<PublicTribute />} />
                        
                        {/* Marketing Pages */}
                        <Route path="/why-living-tribute" element={<WhyLivingTributePage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/resources" element={<ResourcesPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/support" element={<SupportCenter />} />
                        <Route path="/plan-ahead" element={<PlanAheadPage />} />
                        <Route path="/pets" element={<PetsPage />} />
                        <Route path="/gift" element={<GiftTribute />} />
                        <Route path="/tribute-video" element={<TributeVideoPage />} />
                        <Route path="/digital-memorial" element={<DigitalMemorialPage />} />
                        <Route path="/celebration-of-life-video" element={<CelebrationOfLifeVideoPage />} />
                        <Route path="/online-memorial-page" element={<OnlineMemorialPagePage />} />
                        <Route path="/how-to-make-a-tribute-video" element={<HowToMakeTributeVideoPage />} />
                        <Route path="/photos-to-life" element={<PhotosToLifePage />} />
                        <Route path="/collaborate" element={<CollaboratePage />} />
                        <Route path="/craft-obituary" element={<CraftObituaryPage />} />
                        
                        {/* Invites & Quick Drop */}
                        <Route path="/invite/accept" element={<InviteAccept />} />
                        <Route path="/drop/:token" element={<QuickDropPage />} />
                        
                        {/* Partner Branded Landing */}
                        <Route path="/p/:partnerSlug" element={<PartnerLanding />} />
                        
                        {/* Partner Program */}
                        <Route path="/partners" element={<PartnersPage />} />
                        <Route path="/partners/apply" element={<PartnerApply />} />
                        <Route path="/partners/terms" element={<PartnerTermsPage />} />
                        <Route path="/partner/pending" element={<PartnerPending />} />
                        
                        {/* Partner Dashboard Routes */}
                        <Route path="/partner" element={<PartnerDashboard />} />
                        <Route path="/partner/users" element={<PartnerUsers />} />
                        <Route path="/partner/tributes" element={<PartnerTributes />} />
                        <Route path="/partner/revenue" element={<PartnerRevenue />} />
                        <Route path="/partner/settings" element={<PartnerSettings />} />
                        <Route path="/partner/support" element={<PartnerSupport />} />
                        
                        {/* Admin Routes */}
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/users" element={<AdminUsers />} />
                        <Route path="/admin/users/:id" element={<AdminUserDetail />} />
                        <Route path="/admin/tributes" element={<AdminTributes />} />
                        <Route path="/admin/tributes/:id" element={<AdminTributeDetail />} />
                        <Route path="/admin/gifts" element={<AdminGifts />} />
                        <Route path="/admin/partners" element={<AdminPartners />} />
                        <Route path="/admin/partners/:id" element={<AdminPartnerDetail />} />
                        <Route path="/admin/payouts" element={<AdminPayouts />} />
                        <Route path="/admin/payouts/:id" element={<AdminPayoutDetail />} />
                        <Route path="/admin/audit" element={<AdminAuditLogs />} />
                        <Route path="/admin/settings" element={<AdminSettings />} />
                        
                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </TooltipProvider>
              </PartnerBrandingProvider>
            </ImpersonationProvider>
          </AuthProvider>
        </PostHogProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
